import { DestroyRef, Injectable, NgZone, effect, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { ChatsListFilter, ChatsMessagesFilter } from '../../core/classes/Filter';
import { EPusherEvents } from '../../core/enums';
import { IChatsListItem } from '../../core/interfaces';
import { AuthStore } from '../../store/auth.store';
import { ChatsStore } from '../../store/chat.store';
import { PlatformService } from '../platform/platform.service';
import { PusherApiService } from '../pusher-api/pusher-api.service';

@Injectable({
  providedIn: 'root'
})
export class AppResumeSyncService {
  private authStore = inject(AuthStore);
  private chatsStore = inject(ChatsStore);
  private destroyRef = inject(DestroyRef);
  private platform = inject(PlatformService);
  private pusherApi = inject(PusherApiService);
  private zone = inject(NgZone);
  private started = false;
  private lastResumeSyncAt = 0;
  private chatsListRefreshQueued = false;
  private chatsListSubscriptionUserId: number | null = null;
  private readonly resumeSyncThrottleMs = 1500;
  private readonly currentChatSyncedSubject = new Subject<void>();

  currentChatSynced$ = this.currentChatSyncedSubject.asObservable();

  constructor() {
    effect(() => {
      const userId = this.authStore.user()?.id;
      if (this.started && !userId) {
        this.pusherApi.unsubscribeChatsListChannel();
        this.pusherApi.unsubscribeNotificationsChannel();
        this.chatsListSubscriptionUserId = null;
      }

      if (this.started && userId) {
        this.ensureChatsListRealtime();
        this.pusherApi.ensureNotificationsChannel();
        this.queueRefreshChatsListIfNeeded();
      }
    });
  }

  start() {
    if (!this.platform.isPlatformBrowser() || this.started) {
      return;
    }

    this.started = true;
    const syncAfterResume = () => this.syncAfterResume();

    document.addEventListener('visibilitychange', syncAfterResume);
    window.addEventListener('focus', syncAfterResume);
    window.addEventListener('online', syncAfterResume);
    window.addEventListener('pageshow', syncAfterResume);

    this.destroyRef.onDestroy(() => {
      document.removeEventListener('visibilitychange', syncAfterResume);
      window.removeEventListener('focus', syncAfterResume);
      window.removeEventListener('online', syncAfterResume);
      window.removeEventListener('pageshow', syncAfterResume);
    });

    this.refreshChatsListIfNeeded();
    this.ensureChatsListRealtime();
  }

  private syncAfterResume() {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return;
    }

    const now = Date.now();
    if (now - this.lastResumeSyncAt < this.resumeSyncThrottleMs) {
      return;
    }

    this.lastResumeSyncAt = now;

    this.zone.run(() => {
      if (!this.authStore.user()) {
        return;
      }

      this.pusherApi.refreshConnectionOnResume();
      this.ensureChatsListRealtime();
      this.pusherApi.ensureNotificationsChannel();
      this.refreshChatsList();
      this.refreshCurrentChatMessages();
    });
  }

  private ensureChatsListRealtime() {
    const userId = this.authStore.user()?.id;
    if (!userId || (this.chatsListSubscriptionUserId === userId && this.pusherApi.chatsListChannel)) {
      return;
    }

    this.pusherApi.unsubscribeChatsListChannel();
    this.pusherApi.subscribeChatsListChannel(userId);
    this.pusherApi.chatsListChannel?.bind(EPusherEvents.newChatContact, (chatItem: IChatsListItem) => {
      this.zone.run(() => {
        this.chatsStore.checkAndPushNewChatListItem(chatItem);
      });
    });
    this.chatsListSubscriptionUserId = userId;
  }

  private refreshChatsListIfNeeded() {
    if (this.authStore.user() && this.chatsStore.chatsList().length === 0) {
      this.refreshChatsList();
    }
  }

  private queueRefreshChatsListIfNeeded() {
    if (this.chatsListRefreshQueued) {
      return;
    }

    this.chatsListRefreshQueued = true;
    setTimeout(() => {
      this.chatsListRefreshQueued = false;
      this.refreshChatsListIfNeeded();
    }, 0);
  }

  private refreshChatsList() {
    this.chatsStore.getChats(new ChatsListFilter(), this.destroyRef);
  }

  private refreshCurrentChatMessages() {
    const chatId = this.chatsStore.selectedChatId();
    if (!chatId) {
      return;
    }

    this.chatsStore.getMessages(new ChatsMessagesFilter(), chatId, this.destroyRef, () => {
      this.chatsStore.setZeroChatNewMessages(chatId);
      this.currentChatSyncedSubject.next();
    });
  }
}
